import { useCallback, useState } from "react";
import { Api } from "services/api/api.service";
import { AbsenceType } from "model/Schedule";
import type { Result } from "utils/result";
import type { TimeReportErrorKey } from "hooks/timeReportErrors";
import { resolveAlertKey } from "hooks/timeReportErrors";

export interface ReportAbsencePayload {
  companyId: number;
  timeReportId: number;
  comment: string;
  resolution: AbsenceType;
}

export interface UseReportAbsenceResult {
  reportAbsence: (
    payload: ReportAbsencePayload,
  ) => Promise<Result<void, TimeReportErrorKey>>;
  isLoading: boolean;
  error: TimeReportErrorKey | null;
}

interface ApiErrorPayload {
  errorCode?: number;
  errorMessage?: string;
  errorType?: string;
  referenceId?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const resolveApiErrorPayload = (
  error: unknown,
): {
  status?: number;
  data?: ApiErrorPayload;
} => {
  if (!isRecord(error)) {
    console.warn("Report absence error is not an object", error);
    return {};
  }
  const response = isRecord(error.response) ? error.response : null;
  const status =
    response && typeof response.status === "number"
      ? response.status
      : undefined;
  const data =
    response && isRecord(response.data)
      ? (response.data as ApiErrorPayload)
      : undefined;
  return { status, data };
};

const resolveErrorMessage = (
  payload: ApiErrorPayload | undefined,
  status?: number,
): string => {
  if (payload?.errorMessage) return payload.errorMessage;
  if (status && status >= 500) return "Unexpected error";
  return "Unexpected error";
};

export const useReportAbsence = (): UseReportAbsenceResult => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<TimeReportErrorKey | null>(null);

  const reportAbsence = useCallback(
    async (
      payload: ReportAbsencePayload,
    ): Promise<Result<void, TimeReportErrorKey>> => {
      setIsLoading(true);
      setError(null);
      try {
        await Api().company.timeReports.reportAbsence(
          payload.companyId,
          payload.timeReportId,
          payload.comment,
          payload.resolution,
        );
        return { ok: true, value: undefined };
      } catch (err: unknown) {
        const { status, data } = resolveApiErrorPayload(err);
        const message = resolveErrorMessage(data, status);
        const alertKey = resolveAlertKey(message);
        setError(alertKey);
        return { ok: false, error: alertKey };
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return { reportAbsence, isLoading, error };
};
